import { forwardRef } from 'react';
import {
  Platform,
  TextInput as RNTextInput,
  Text,
  View,
  type TextInputProps as RNTextInputProps,
} from 'react-native';
import { twMerge } from 'tailwind-merge';
import { webPrivacyProps } from '../lib/privacy';

export interface TextInputProps extends RNTextInputProps {
  /** Label rendered above the input */
  label?: string;
  /** Error message — shows red border and message below */
  error?: string | null;
  /** Hint text below the input (hidden when error is shown) */
  hint?: string;
  /** Inline prefix (e.g. "$") rendered inside the input */
  prefix?: string;
  /** Input size variant */
  size?: 'sm' | 'md';
  /** className for the outer container */
  containerClassName?: string;
  /** className merged onto the input element */
  inputClassName?: string;
}

export const TextInput = forwardRef<RNTextInput, TextInputProps>(
  function TextInput(
    {
      label,
      error,
      hint,
      prefix,
      size = 'md',
      containerClassName,
      inputClassName,
      editable,
      className,
      ...rest
    },
    ref,
  ) {
    const { dataSet, ...inputRest } = rest as RNTextInputProps & {
      dataSet?: Record<string, string | number | boolean>;
    };
    const sizeClasses = size === 'sm' ? 'px-3 py-2 text-sm' : 'px-4 py-3 text-base';
    const disabled = editable === false;
    const privacyProps = webPrivacyProps('sensitive', dataSet);

    const inputClasses = twMerge(
      'rounded-xl text-gray-800 dark:text-gray-100 bg-white dark:bg-gray-800',
      'border',
      error ? 'border-red-400 dark:border-red-500' : 'border-gray-200 dark:border-gray-600',
      sizeClasses,
      disabled && 'opacity-50',
      prefix && 'pl-0',
      inputClassName,
      className,
    );

    const input = (
      <RNTextInput
        ref={ref}
        editable={editable}
        placeholderTextColor="#9ca3af"
        className={inputClasses}
        {...privacyProps}
        {...inputRest}
      />
    );

    return (
      <View className={containerClassName}>
        {label && (
          <Text className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
            {label}
          </Text>
        )}
        {prefix ? (
          <View
            className={twMerge(
              'flex-row items-center rounded-xl bg-white dark:bg-gray-800 border',
              error ? 'border-red-400 dark:border-red-500' : 'border-gray-200 dark:border-gray-600',
              disabled && 'opacity-50',
            )}
          >
            <Text
              className={twMerge(
                'text-gray-500 dark:text-gray-400 font-medium',
                size === 'sm' ? 'pl-3 text-sm' : 'pl-4 text-base',
              )}
            >
              {prefix}
            </Text>
            <RNTextInput
              ref={ref}
              editable={editable}
              placeholderTextColor="#9ca3af"
              className={twMerge(
                'flex-1 text-gray-800 dark:text-gray-100',
                size === 'sm' ? 'px-1 py-2 text-sm' : 'px-1 py-3 text-base',
                inputClassName,
                className,
              )}
              {...(Platform.OS === 'web' ? { style: { outline: 'none' } as any } : {})}
              {...privacyProps}
              {...inputRest}
            />
          </View>
        ) : (
          input
        )}
        {error && (
          <Text className="text-xs text-red-600 dark:text-red-400 mt-1">{error}</Text>
        )}
        {!error && hint && (
          <Text className="text-xs text-gray-400 dark:text-gray-500 mt-1">{hint}</Text>
        )}
      </View>
    );
  },
);
